/**
 * Sport-by-sport fundraising guides — /resources/fundraising-guides/:slug.
 *
 * One record per sport; one page template renders them all. Each record must be
 * genuinely sport-specific (its own costs, calendar and money moments, every
 * number sourced) — a guide that only swaps the sport's name is thin content
 * and should not ship. Adding a sport: append here, then add its route to
 * PRERENDER_ROUTES in vite.config.ts and a <url> to public/sitemap.xml
 * (src/lib/data/contentHub.test.ts fails until you do).
 */
export interface SportCostLine {
  item: string;
  cost: string;
}

export interface SportGuide {
  slug: string;
  /** Matches championships.ts / grants.ts sport slugs (for cross-linking). */
  sport: string;
  name: string;
  /** e.g. "travel baseball" — used in sentences. */
  noun: string;
  image: string;
  /** Meta description (≤ 160 chars). */
  description: string;
  /** One-paragraph intro: who this is for and the money reality. */
  intro: string;
  /** Headline yearly cost range for a competitive/club athlete. */
  costRange: string;
  /** A full sentence citing the all-levels survey average, if published. */
  averageSpend?: string;
  costLines: SportCostLine[];
  /** When money is needed across a year. */
  calendar: { when: string; what: string }[];
  /** The concrete moments a family suddenly needs money. */
  moneyMoments: string[];
  /** Fundraising angles that fit this sport. */
  angles: { title: string; body: string }[];
  /** Worked example goal, split into a monthly number. */
  sampleGoal: { total: number; months: number; lines: SportCostLine[] };
  faqs: { q: string; a: string }[];
  sources: { label: string; url: string }[];
}

export const sportGuides: SportGuide[] = [
  {
    slug: 'travel-baseball',
    sport: 'baseball',
    name: 'Travel Baseball',
    noun: 'travel baseball',
    image: '/resources/team-huddle.jpg',
    description:
      'What a travel baseball season really costs in 2026 — team fees, tournaments, bats, Cooperstown — when the bills land, and fundraising ideas that fit the sport.',
    intro:
      "Travel ball means more games, more tournaments and a lot more driving than rec league — and a bill that keeps arriving all summer. Here's what it really costs, when the money is due, and how to raise it without selling another candle.",
    costRange: '$2,000–$5,000+',
    averageSpend:
      "Across all levels, families spent an average of $1,113 a year on baseball in the Aspen Institute's 2024 Project Play survey — up from $660 in 2019. Travel ball runs well above that average.",
    costLines: [
      { item: 'Travel-ball team costs (parent survey average, before uniforms)', cost: '≈ $2,180 / year' },
      { item: 'Showcase or tournament entry, per team (split across the roster)', cost: '$750–$2,000 per event' },
      { item: 'Destination week, e.g. a 12U Cooperstown tournament (team-page estimates)', cost: '≈ $1,000 per player for the week; ≈ $5,000 per family all-in with travel' },
      { item: 'BBCOR bat for high school (2026 list prices)', cost: '$350–$450' },
      { item: 'USA youth bat (2026 models)', cost: '$150–$400' },
    ],
    calendar: [
      { when: 'November – December', what: 'Tryouts and commitments for spring teams — the first deposits are due here.' },
      { when: 'April – June', what: 'Spring season: weekend tournaments, often several hours away.' },
      { when: 'Memorial Day – mid-July', what: 'Summer travel: back-to-back tournament weekends and destination events — the most expensive stretch.' },
      { when: 'May – June', what: 'Tryouts for fall teams.' },
      { when: 'September – October', what: 'Fall ball and showcase season for older players.' },
    ],
    moneyMoments: [
      'The commitment deposit, due days after a tryout offer.',
      'A destination tournament (Cooperstown, a World Series, a big showcase) — a fixed date and a fixed price months ahead.',
      'Moving up to high school and needing a BBCOR bat.',
      'The summer stretch, when hotel nights stack up weekend after weekend.',
      'Recruiting showcases for 15U–17U players, which add entry fees and travel on top of the team schedule.',
    ],
    angles: [
      {
        title: 'Name the big tournament',
        body: '"Help me get to Cooperstown" outraises "support my season" because it has a date, a place and a price. Put the tournament, the cost and the deadline at the top of the fund page.',
      },
      {
        title: 'Sponsor a tournament weekend',
        body: 'Break the summer into weekends — "$180 covers one tournament weekend\'s hotel" — and let each supporter pick one. Thank them by name in that weekend\'s update.',
      },
      {
        title: 'Start the monthly ask in the off-season',
        body: 'The bills land in summer, but the deposit is due in winter. Ask for $20–$25 a month in November, and by June the money is already there instead of being a scramble.',
      },
      {
        title: 'A local business sponsor',
        body: 'A hardware store or restaurant may back one player for a season — check your league\'s rules before adding any logo to gear. The free sponsorship letter template makes the ask easy.',
      },
    ],
    sampleGoal: {
      total: 3000,
      months: 8,
      lines: [
        { item: 'Team fees and dues', cost: '$1,200' },
        { item: 'Share of tournament entry fees', cost: '$500' },
        { item: 'Six hotel nights across the summer', cost: '$900' },
        { item: 'New BBCOR bat', cost: '$400' },
      ],
    },
    faqs: [
      {
        q: 'How much does travel baseball cost per year?',
        a: 'A survey of about 700 travel-ball parents put team costs at roughly $2,180 a year before uniforms. Add hotels, gas, bats and any destination tournament, and many families spend $3,000–$5,000 or more.',
      },
      {
        q: 'How do families pay for a Cooperstown tournament?',
        a: 'Team fundraiser pages put the week at about $1,000 per player, and families estimate around $5,000 all-in with travel. Because it is booked months ahead, it is the easiest thing in travel baseball to fundraise for — a named event, a fixed date and a clear cost.',
      },
      {
        q: 'Can a travel baseball player fundraise individually, not just with the team?',
        a: "Yes. Team fundraisers cover team costs; a player's own fund page covers what the family pays — travel, hotels, bats, private lessons. With Sport Fund the athlete keeps 100% of every donation, and supporters can give monthly.",
      },
    ],
    sources: [
      { label: 'Aspen Institute Project Play 2024 family spending survey', url: 'https://projectplay.org/news/2025/2/24/project-play-survey-family-spending-on-youth-sports-rises-46-over-five-years' },
      { label: 'BatDigest travel-ball parent survey', url: 'https://batdigest.com/blog/is-travel-ball-worth-it/' },
      { label: 'Perfect Game event listings', url: 'https://www.perfectgame.org/events/Default.aspx?event=2994' },
      { label: 'BatDigest 2026 USA bat guide', url: 'https://www.batdigest.com/best-usa-bats' },
      { label: 'National Pastime Museum 2026 BBCOR prices', url: 'https://www.thenationalpastimemuseum.com/?p=5258' },
    ],
  },
  {
    slug: 'club-volleyball',
    sport: 'volleyball',
    name: 'Club Volleyball',
    noun: 'club volleyball',
    image: '/resources/athlete-celebration.jpg',
    description:
      'What club volleyball really costs in 2026 — club fees, deposits, tournament travel and nationals — plus when the money is due and how to fundraise for it.',
    intro:
      "Club tryouts land right after the high-school season, and the deposit is due within days of the offer. Here's what a club season costs, when each bill arrives, and how to fund it — including the summer nationals trip the club fee doesn't cover.",
    costRange: '$1,100–$4,800+',
    averageSpend:
      "For comparison, the Aspen Institute's 2019 survey put the average across all levels of volleyball at $595 a year — a number that includes school and rec players, and sits far below what club families pay.",
    costLines: [
      { item: 'Regional club fee, per season', cost: '$400–$2,700' },
      { item: 'National-level club fee (2025–26 example; covers uniforms, entries, gym and coach travel)', cost: '$4,700–$4,800' },
      { item: 'Commitment deposit after tryouts (example)', cost: '≈ $600' },
      { item: 'Monthly payment plan on a national-level team (example)', cost: '≈ $455–$470 / month' },
      { item: "Family travel to tournaments and nationals — not in the club fee", cost: 'Flights, hotels and food on top' },
    ],
    calendar: [
      { when: 'Late October – mid-November', what: 'Tryouts, right after the high-school season. Offers and commitment letters come fast.' },
      { when: 'November – July', what: 'The club season: practices plus regional and national-qualifier tournaments.' },
      { when: 'Monthly, from late summer or fall', what: 'Payment plans on many clubs, spreading the fee across the season.' },
      { when: 'May – early July', what: 'National championships — USA Volleyball and AAU nationals in cities like Las Vegas, Chicago, Minneapolis and Orlando.' },
    ],
    moneyMoments: [
      'The deposit and commitment letter, often due within days of a tryout offer.',
      'Monthly club dues that run for most of the year.',
      'Out-of-town qualifier tournaments with a hotel weekend each.',
      'The nationals bid — a week of flights and hotels in June or July that the club fee usually does not cover.',
    ],
    angles: [
      {
        title: 'Ask now, before the deposit',
        body: 'Tryouts are in late fall, so the moment to set up a fund page is before them — supporters who start at $25 a month in October have the deposit and first dues covered by the time the offer comes.',
      },
      {
        title: '"Get me to nationals"',
        body: 'Once the team earns a bid, the trip has a city, a week and a price. Use the nationals trip calculator, put the number on the page, and send it to the family group chat the day the bid comes in.',
      },
      {
        title: 'Match monthly supporters to monthly dues',
        body: 'If dues are about $450 a month, eighteen supporters at $25 a month cover them. That is a clear, honest ask: "Will you be one of my eighteen?"',
      },
      {
        title: 'Sponsor a tournament weekend',
        body: "List the season's travel tournaments and let a grandparent or local business \"own\" one. Post the result and a photo afterwards so they see what they paid for.",
      },
    ],
    sampleGoal: {
      total: 4000,
      months: 9,
      lines: [
        { item: 'Club fee balance after the deposit', cost: '$2,500' },
        { item: 'Three out-of-town tournament weekends', cost: '$900' },
        { item: 'Share of the nationals trip', cost: '$600' },
      ],
    },
    faqs: [
      {
        q: 'How much does club volleyball cost?',
        a: 'Regional club fees commonly run $400–$2,700 a season, and many families spend $1,100–$4,000 or more all-in. National-level teams can charge $4,700–$4,800 in club fees alone, before family travel to tournaments and nationals.',
      },
      {
        q: 'When are club volleyball tryouts?',
        a: 'Usually late October to mid-November, right after the high-school season ends. Clubs often ask for a signed commitment and a deposit within days of an offer, so it pays to plan the money before tryouts.',
      },
      {
        q: "Does the club fee cover nationals?",
        a: "Club fees often include tournament entries and coaches' travel, but rarely the family's own flights, hotel and food. A nationals trip is a separate, predictable cost — and one of the easiest to fundraise for.",
      },
    ],
    sources: [
      { label: 'Country Fire Volleyball 16U and 18U tryout pages (2025–26 fees)', url: 'https://countryfirevolleyball.com/tryouts/16u-tryouts' },
      { label: 'Better at Volleyball cost estimate', url: 'https://betteratvolleyball.com/how-much-does-club-volleyball-cost-and-is-it-worth-it/' },
      { label: 'NCVA tryout rules', url: 'https://ncva.com/?p=129701' },
      { label: 'Aspen Institute / Utah State 2019 youth sport survey (via Money)', url: 'https://money.com/youth-sports-cost/' },
      { label: 'USA Volleyball junior nationals', url: 'https://usavolleyball.org/?p=48357' },
    ],
  },
  {
    slug: 'club-soccer',
    sport: 'soccer',
    name: 'Club Soccer',
    noun: 'club soccer',
    image: '/resources/coach-and-athlete.jpg',
    description:
      'What club soccer costs in 2026 — ECNL and regional league fees, kits, showcases and summer playoffs — when the bills land, and how to fundraise for the season.',
    intro:
      "Club soccer runs almost all year, and the fee you see at tryouts is only part of it — kits, showcases and summer playoff travel come on top. Here's the real cost, the money calendar, and fundraising that fits.",
    costRange: '$2,000–$12,000+',
    averageSpend:
      "Across all levels, rec included, families spent an average of $910 a year on soccer in the Aspen Institute's 2024 Project Play survey (up from $537 in 2019).",
    costLines: [
      { item: 'ECNL club fees, 2025–26 (examples from club fee pages)', cost: '$2,700–$4,495 / year' },
      { item: 'ECNL Regional League fees (example)', cost: '≈ $2,280 + $580 fall registration' },
      { item: 'Monthly dues plan (example: 11 months, June – April)', cost: '$300 / month' },
      { item: 'Uniform kit', cost: '$385–$436' },
      { item: 'All-in at ECNL / MLS NEXT level (estimate)', cost: '$5,000–$12,000+ / year' },
    ],
    calendar: [
      { when: 'May – June', what: 'Tryouts. An offer usually comes with the first dues payment and a kit order.' },
      { when: 'August – June', what: 'Fall and spring seasons: league games, travel and college showcases for older players.' },
      { when: 'Monthly', what: 'Dues plans that spread the club fee across 10–11 months.' },
      { when: 'July', what: 'National playoffs and finals — ECNL and US Youth Soccer nationals.' },
    ],
    moneyMoments: [
      'The first dues payment and the kit order, right after a tryout offer.',
      'College showcase and ID events for high-school-age players.',
      'Summer playoff and national-championship travel.',
      'A team change: from 2026–27, US Youth Soccer, AYSO and US Club Soccer group players by school year instead of birth year, so many players are changing age group or team this year — and paying new kit and fees.',
    ],
    angles: [
      {
        title: 'Turn the dues into a monthly ask',
        body: 'If dues are $300 a month, twelve supporters at $25 cover them. Monthly support lines up with how club soccer actually bills — and it is easier to say yes to than a $3,000 ask.',
      },
      {
        title: 'Sponsor a showcase weekend',
        body: 'For high-school players, each college showcase has a cost and a purpose. "Help me get seen by college coaches in March" is a concrete, shareable ask.',
      },
      {
        title: 'The kit sponsor',
        body: 'A new kit is a one-time, easy-to-picture cost — a natural ask for a grandparent or a family friend who wants to give something specific.',
      },
      {
        title: 'Plan for July now',
        body: 'If your team has a shot at playoffs or nationals, add the trip to the goal early. Use the nationals trip calculator to price it.',
      },
    ],
    sampleGoal: {
      total: 5000,
      months: 10,
      lines: [
        { item: 'Club dues (11 months)', cost: '$3,300' },
        { item: 'Uniform kit', cost: '$400' },
        { item: 'Two showcase weekends', cost: '$900' },
        { item: 'Summer playoff travel', cost: '$400' },
      ],
    },
    faqs: [
      {
        q: 'How much does ECNL soccer cost?',
        a: 'ECNL club fees on 2025–26 club fee pages ranged from about $2,700 to $4,500 a year, plus kit and registration fees. Counting travel, showcases and summer playoffs, estimates for ECNL and MLS NEXT families run $5,000–$12,000 or more a year.',
      },
      {
        q: 'Why are so many players changing teams in 2026–27?',
        a: 'Starting with the 2026–27 season, US Youth Soccer, AYSO and US Club Soccer group players by school year (August 1 – July 31) rather than birth year. Many players move to a new age group, and often a new team — with new fees and kit.',
      },
      {
        q: 'Can a club soccer player fundraise on their own?',
        a: "Yes. A player's fund page covers what the family pays — dues, kit, showcase travel — and is separate from any team fundraiser. With Sport Fund the athlete keeps 100% of every donation, and supporters can give monthly.",
      },
    ],
    sources: [
      { label: 'Aspen Institute Project Play 2024 family spending survey', url: 'https://projectplay.org/news/2025/2/24/project-play-survey-family-spending-on-youth-sports-rises-46-over-five-years' },
      { label: 'Davis Legacy SC 2025–26 player dues', url: 'https://davislegacysoccer.org/wp-content/uploads/2025/04/DLSC-2025-26-Player-Dues.pdf' },
      { label: 'WSA ECNL-RL financials', url: 'https://wsasoccer.org/club-info/about-wsa/tryouts/financials/wsa-girls-ecnl-rl' },
      { label: 'SF Elite fees', url: 'https://www.sfelitesc.org/test1' },
      { label: 'MyClubScout ECNL cost estimate', url: 'https://myclubscout.com/blog/what-is-ecnl' },
      { label: 'School-year age groups from 2026–27 (TCSL)', url: 'https://www.tcslsoccer.com/changes-in-youth-soccer-age-group-change-effective-with-2026-27-soccer-year/' },
    ],
  },
  {
    slug: 'competitive-swimming',
    sport: 'swimming',
    name: 'Competitive Swimming',
    noun: 'competitive swimming',
    image: '/resources/athlete-training.jpg',
    description:
      'What competitive club swimming costs — monthly dues, USA Swimming membership, meet fees and championship travel — and how swimmers fundraise for the season.',
    intro:
      "Club swimming looks cheap per month and adds up fast: dues every month of the year, a meet entry most weekends, and championship travel once a swimmer starts making cuts. Here's the real cost and how to fund it.",
    costRange: '$1,000–$3,500+ before travel',
    averageSpend:
      "For comparison, the Aspen Institute's 2019 survey put the average across all levels of swimming at $786 a year — competitive club swimmers spend well beyond that once dues run twelve months a year.",
    costLines: [
      { item: 'Club dues (club fee pages, by training group)', cost: '$65–$240 / month' },
      { item: 'USA Swimming membership (as quoted on club pages — check the current price)', cost: '≈ $70 Premium · ≈ $30 Flex' },
      { item: 'Meet entry', cost: '≈ $30–$50 per meet; more for championship meets' },
      { item: 'Championship travel (Futures, Junior Nationals, sectionals)', cost: 'Flights and hotel for 3–5 days' },
      { item: 'Tech suit for championship meets', cost: 'A significant one-time cost — price yours before setting the goal' },
    ],
    calendar: [
      { when: 'September – March', what: 'Short-course season (25-yard pools): dues, regular meets and winter championships.' },
      { when: 'December', what: 'Winter championships, including Winter Junior Nationals for the fastest 18-and-unders.' },
      { when: 'April – August', what: 'Long-course season (50-meter pools), building to summer championships.' },
      { when: 'Late July – early August', what: 'Futures and Junior Nationals for swimmers who make the cuts.' },
    ],
    moneyMoments: [
      'Moving up a training group, which raises the monthly dues.',
      'Making a cut — a qualifying time for a championship meet, often in another state, weeks before it happens.',
      'Buying a tech suit for championship season.',
      'USA Swimming membership renewal.',
    ],
    angles: [
      {
        title: 'Sponsor the yards',
        body: 'Swimmers log huge training volume. "$10 a month sponsors 10,000 yards" turns invisible early-morning practices into something supporters can follow in each update.',
      },
      {
        title: 'Fund the cut',
        body: 'The moment a swimmer makes a Futures or Junior Nationals cut is the best fundraising moment of the year: a named meet, a date, a city. Price the trip with the nationals calculator and share it the same day.',
      },
      {
        title: 'Monthly supporters for monthly dues',
        body: 'Dues run twelve months a year, so monthly support fits swimming better than any one-time ask. Ask a handful of relatives to each cover one month.',
      },
    ],
    sampleGoal: {
      total: 2800,
      months: 12,
      lines: [
        { item: 'Club dues ($150 × 12)', cost: '$1,800' },
        { item: 'Meet entries across the season', cost: '$400' },
        { item: 'USA Swimming membership', cost: '$70' },
        { item: 'Championship meet travel', cost: '$530' },
      ],
    },
    faqs: [
      {
        q: 'How much does competitive swimming cost a year?',
        a: 'Club dues alone run about $65–$240 a month depending on the training group, so $780–$2,880 a year. Add USA Swimming membership, meet entries of roughly $30–$50 each, a tech suit, and championship travel, and families commonly spend $1,000–$3,500 or more.',
      },
      {
        q: 'When do swimmers need the most money?',
        a: 'When they make a championship cut. Qualifying for Winter Juniors in December or Futures and Junior Nationals in summer usually means a multi-day trip in another state on short notice.',
      },
      {
        q: 'Can a swimmer have their own fund page?',
        a: 'Yes. A swimmer — or a parent, for minors — can set up a free Sport Fund page. Supporters can give once or monthly, and 100% of every donation reaches the athlete.',
      },
    ],
    sources: [
      { label: 'Club dues and fee pages (gomotionapp.com: MNVA, VJO, SCSDA, INWAS)', url: 'https://www.gomotionapp.com/team/vjo/page/swim-team/25-26-costs' },
      { label: 'Aspen Institute / Utah State 2019 youth sport survey (via Money)', url: 'https://money.com/youth-sports-cost/' },
      { label: 'USA Swimming 2027 domestic competition schedule', url: 'https://www.usaswimming.org/news/2026/05/19/usa-swimming-announces-2027-domestic-competition-schedule' },
    ],
  },
];

export function findSportGuide(slug: string | undefined): SportGuide | undefined {
  return sportGuides.find((g) => g.slug === slug);
}
